import {useParams} from "react-router";
import React, {useEffect, useRef, useState} from 'react';
import PropTypes from "prop-types";
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {
  getAlertCorrelation,
  getAlertLogDetails, getAlertLogStatusHistory,
  getErrorLogs,
  getMetricDataByAlertLogId,
  getRunbook, getRunbookStepsResultsByAlertLogId,
  postAcknowledgeAlert,
  postResolveAlert
} from "../../store/alertlog/actions";
import {getRecentChangeEvents} from "../../store/organization/actions"
import {getOrganization} from "store/actions";
import {useDispatch, useSelector} from "react-redux";
import {
  Badge,
  Button,
  CardBody,
  Col,
  Collapse,
  Container, Modal, ModalBody, ModalFooter, ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Popover,
  PopoverBody,
  Row,
  TabContent, Table,
  TabPane,
  UncontrolledTooltip
} from "reactstrap";
import Card from "components/PurpleTheme/Card";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {getIntegrationLogo, getLocalDateTime} from "../../helpers/component_helpers";


import Runbook from "components/Runbooks/Runbook";
import CreateRunbook from "components/Runbooks/CreateRunbook";
import classnames from "classnames";
import RootCauseAnalysis from "../../components/RootCauseAnalysis";

function get_integration_logo(key) {
  switch (key) {
    case "NewRelic":
      return <img src="/icons/newrelic.svg" alt="Customize Icon" className="me-2 svg-icon"/>;
    case "CloudTrail":
      return <i className="fab fa-aws me-2"></i>;
    case "Sentry":
      return <img src="/icons/sentry.svg" alt="Customize Icon" className="me-2 svg-icon"/>;
    case "Loki":
      return <img src="/icons/loki.svg" alt="Customize Icon" className="me-2 svg-icon"/>;
    case "Coralogix":
      return <img src="/icons/coralogix.svg" alt="Customize Icon" className="me-2 svg-icon"/>;
    default:
      return null;
  }
}


const AlertLogDetails = () => {

  const {id} = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.includes(`/alert-log/${id}/ack`)) {
      const acknowledgeAndFetchDetails = async () => {
        try {
          let acknowledgePayload = {
            'alert_ids': [id]
          };
          await dispatch(postAcknowledgeAlert(acknowledgePayload));
          dispatch(getAlertLogDetails(id));
        } catch (error) {
          console.error('Error acknowledging or fetching alert details:', error);
        }
      }
      acknowledgeAndFetchDetails();
    } else if (location.pathname.includes(`/alert-log/${id}/resolve`)) {
      const resolvedAndFetchDetails = async () => {
        try {
          let resolvedPayload = {
            'alert_ids': [id]
          };
          await dispatch(postResolveAlert(resolvedPayload));
          dispatch(getAlertLogDetails(id));
        } catch (error) {
          console.error('Error acknowledging or fetching alert details:', error);
        }
      }
      resolvedAndFetchDetails();
    }
  }, [location, id]);

  const dispatch = useDispatch();

  const {alertStatusHistory} = useSelector(state => ({
    alertStatusHistory: state.AlertLog.alertStatusHistory
  }));

  const {alert} = useSelector(state => ({alert: state.AlertLog.alert}));

  const {runbook} = useSelector(state => ({runbook: state.AlertLog.runbook}))

  const {alert_correlation_data} = useSelector(state => ({
    alert_correlation_data: state.AlertLog.alert_correlation_data
  }))

  const {error_logs_by_alert_log} = useSelector(state => ({
    error_logs_by_alert_log: state.AlertLog.error_logs_by_alert_log
  }));

  const {recentChangeEvents} = useSelector(state => ({
    recentChangeEvents: state.Organization.recentChangeEvents || [],
  }))

  const [runbookView, setRunbookView] = useState(false);
  const [createRunbookView, setCreateRunbookView] = useState(false);
  const runbookRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentRunbookIndex, setCurrentRunbookIndex] = useState(0);
  const [showAllMetrics, setShowAllMetrics] = useState(false);


  // Note: The following is a feature flag and should be removed once testing completed
  // in production
  const feature_flag_runbook_available_organization_ids = [
    '9900b8df-4163-4e92-ad94-4e907c895c77', 'b7a41007-7d2e-4459-b951-c7748683ef73',
    '6f580899-6e8e-438a-b447-63a596f249f1', 'deb9b9a4-5edb-4905-891c-2b838f5c86c5',
    '6d1a764b-5d54-4350-b0c8-7471e8ed739a', 'ddf8f754-2cc4-4ac0-bcb1-4e11f434f186',
    '9da6bfd1-c5ed-4def-9be0-09640109fbda', '0ac31c82-fdd6-47ab-a851-1609852252c4',
    'b1be49a2-15af-4fa2-b888-40792cfd4e8c', 'c23b9c0a-0286-4899-878e-d92c2a1608ec'];

  useEffect(() => {
    dispatch(getAlertLogDetails(id));
  }, [getAlertLogDetails]);
  useEffect(() => {
    dispatch(getMetricDataByAlertLogId(id));
  }, [dispatch, id]);

  useEffect(() => {
    const alertLogCompletedStatus = ["Ran Diagnostics", 'Ok'];
    let intervalRef = null;
    const isAlertOngoing = () => {
      const latestAlertStatus = alert?.status || '';
      if (latestAlertStatus === 'Ok') {
        return false
      }
      const sortedStatusHistory = alertStatusHistory?.status_history?.slice().sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      const latestStatus = sortedStatusHistory?.[0]?.status;
      if (alertLogCompletedStatus.includes(latestStatus)) {
        return false
      }

      // If the alert has been opened for more than 30 minutes then consider it as not ongoing
      const openedAt = new Date(alert?.opened_at);
      const now = new Date();
      const timeDiff = Math.abs(now - openedAt);
      const diffInMinutes = Math.floor(timeDiff / (1000 * 60));
      return diffInMinutes <= 30;
    };

    const fetchData = () => {
      if (id !== undefined) {
        dispatch(getAlertLogStatusHistory(id));
        dispatch(getMetricDataByAlertLogId(id));
        dispatch(getRunbook(id));
        dispatch(getAlertLogDetails(id));
        dispatch(getRunbookStepsResultsByAlertLogId(id))
      }
    };
    if (id !== undefined) {
      intervalRef = setInterval(() => {
        if (isAlertOngoing()) {
          fetchData();
        } else {
          clearInterval(intervalRef);
        }
      }, 10000);
    }

    return () => {
      if (intervalRef) {
        clearInterval(intervalRef);
      }
    };
  }, [id, alert]);

  useEffect(() => {
    dispatch(getAlertLogStatusHistory(id));
  }, [getAlertLogStatusHistory]);

  useEffect(() => {
    dispatch(getOrganization())
    dispatch(getRunbook(id))
    dispatch(getAlertCorrelation(id))
    dispatch(getErrorLogs(id))
    dispatch(getRecentChangeEvents(id))
    dispatch(getRunbookStepsResultsByAlertLogId(id))
  }, [id])


  const handlePageContentClick = (event) => {
    if (runbookView && runbookRef.current && !runbookRef.current.contains(event.target)) {
      setRunbookView(false);
    }
  };

  const handleAcknowledgeAlerts = async () => {
    let payload = {
      'alert_ids': [id]
    }
    try {
      await dispatch(postAcknowledgeAlert(payload));
      await dispatch(getAlertLogDetails(id));
    } catch (error) {
      console.error('Error resolving alert:', error);
    }
  };

  const handleResolveAlerts = async () => {
    let payload = {
      'alert_ids': [id]
    }
    try {
      await dispatch(postResolveAlert(payload));
      await dispatch(getAlertLogDetails(id));
    } catch (error) {
      console.error('Error resolving alert:', error);
    }
  }

  const viewRunbook = () => {
    runbookView === false ? setRunbookView(true) : setRunbookView(false);
  }
  const [popoverOpen, setPopoverOpen] = useState({});

  const togglePopover = (key, isOpen) => {
    setPopoverOpen(prevState => ({
      ...prevState,
      [key]: isOpen,
    }));
  };

  function handleViewCorrelationClick() {
    navigate(`/alert-log-correlation/${id}`);
  }

  function handleViewAnalysisClick() {
    const rootCauseId = alert_correlation_data?.root_cause_alert_details?.id;

    if (rootCauseId) {
      navigate(`/alert-log-analysis/${rootCauseId}`);
    } else {
      navigate(`/alert-log-analysis/${id}`);
    }
  }

  const handleMouseEnter = (key) => {
    togglePopover(key, true);
  };
  const handleMouseLeave = (key) => {
    togglePopover(key, false);
  };


  const toggleTab = tab => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
    if (tab === "1") {
      setActiveTabName("NewRelic");
    }
    if (tab === "2") {
      setActiveTabName("CloudTrail");
    }
    if (tab === "3") {
      setActiveTabName("Sentry");
    }
    if (tab === "4") {
      setActiveTabName("Loki");
    }
    if (tab === "5") {
      setActiveTabName("Coralogix");
    }
    if (tab === "6") {
      setActiveTabName("ChangeEvents");
    }
  };

  const [expandedLogs, setExpandedLogs] = useState(new Set());

  function toggleExpandLog(index) {
    setExpandedLogs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index); // Collapse if already open
      } else {
        newSet.add(index); // Expand if closed
      }
      return newSet;
    });
  }

  const [modalOpenAnalysis, setModalOpenAnalysis] = useState(false);

  const toggleModal = () => {
    setModalOpenAnalysis(!modalOpenAnalysis);
    setShowAllMetrics(!modalOpenAnalysis);
  };

  function formatTimestampToLogTime(utcDateTime) {
    if (!utcDateTime) return "";
    try {
      const date = new Date(utcDateTime);
      if (isNaN(date.getTime())) {
        return "";
      }
      const adjustedDate = new Date(date.getTime() + (5 * 60 + 30) * 60 * 1000);
      const year = adjustedDate.getFullYear();
      const month = String(adjustedDate.getMonth() + 1).padStart(2, '0');
      const day = String(adjustedDate.getDate()).padStart(2, '0');
      const hours = String(adjustedDate.getHours()).padStart(2, '0');
      const minutes = String(adjustedDate.getMinutes()).padStart(2, '0');
      const seconds = String(adjustedDate.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.error("Error formatting timestamp:", error);
      return "";
    }
  }

  const cluoudtrailHeaders = ['Event Id', 'Event Name', 'Event Source', 'Event Time', 'Username', 'Read Only', 'Region', 'Resource Type', 'Resource Name']
  const integratedSystems = error_logs_by_alert_log['integrated_systems'];

  const logs = [
    {key: "NewRelic", label: "NewRelic Logs", tabId: "1"},
    {key: "CloudTrail", label: "CloudTrail Logs", tabId: "2"},
    {key: "Sentry", label: "Sentry Logs", tabId: "3"},
    {key: "Loki", label: "Loki Logs", tabId: "4"},
    {key: "Coralogix", label: "Coralogix Logs", tabId: "5"},
    {key: "ChangeEvents", label: "Recent Deployments", tabId: "6"}
  ];
  const availableLogs = [
    ...logs.filter(log => integratedSystems[log.key]),
    logs.find(log => log.key === 'ChangeEvents')
  ].filter(Boolean);
  const widthPercentage = availableLogs.length ? 100 / availableLogs.length : 100;
  const defaultTab = null;
  const defaultTabName = null;
  const [activeTab, setActiveTab] = useState(null);
  const [activeTabName, setActiveTabName] = useState(null);
  const [hasInitiallySelectedTab, setHasInitiallySelectedTab] = useState(false);

  useEffect(() => {
    const hasLogs = Object.values(error_logs_by_alert_log).some(logs => logs.length > 0);
    if (hasLogs && !hasInitiallySelectedTab) {
      const logWithLogs = logs.find(log =>
        error_logs_by_alert_log[log.key] &&
        error_logs_by_alert_log[log.key].length > 0
      );
      if (logWithLogs) {
        toggleTab(logWithLogs.tabId);
        setHasInitiallySelectedTab(true);
      }
    }
  }, [error_logs_by_alert_log, hasInitiallySelectedTab]);

  return (
    <React.Fragment>
      <div onClick={handlePageContentClick} className="page-content">
        <Container fluid={true}>
          {/* Render Breadcrumb */}
          <Row>
            <Col lg="8">
              <Breadcrumbs
                title={"Alert Log"}
                breadcrumbItem={"Alert Log"}
              />
            </Col>
          </Row>
          <div className="mb-1 mb-md-3 d-inline-flex d-md-flex custom-width gap-2 gap-md-0">
            {!createRunbookView && (
              <>
                <Col md={1} lg={1} className="custom-width ms-2 ms-md-2 me-lg-3">
                  <div className='mb-3'>
                    <button type="button" className="btn btn-primary w-md custom-width" onClick={() => {
                      handleAcknowledgeAlerts()
                    }} disabled={alert?.status === 'Ok' || alert?.status === 'Acknowledged'}>
                      Acknowledge
                    </button>
                  </div>
                </Col>
                <Col md={1} lg={1} className="custom-width me-2 me-md-0 ms-lg-2">
                  <div className='mb-3'>
                    <button type="button" className="btn btn-primary w-md custom-width" onClick={() => {
                      handleResolveAlerts()
                    }} disabled={alert?.status === 'Ok'}>
                      Resolve
                    </button>
                  </div>
                </Col>
              </>
            )}
          </div>
          <Card>
            <CardBody>
              <div className="d-flex w-100 d-flex-sm-column" style={{border: "none"}}>
                {!createRunbookView ? (
                  <>
                    <Container className="table-purple-org custom-width" style={{width: '50%', backgroundColor: "transparent", borderRadius: "10px"}}>
                      <Row className="p-2 d-flex align-items-center">
                        <Col xs="4" className="">
                          <Row className="d-flex align-items-center">
                            <Col xs="4" className="">Status</Col>
                            <Col xs="4" className="text-start">
                              {alert?.status === "Alarm" ? (
                                <Badge color="danger" className="blog-badge">{alert?.status}</Badge>
                              ) : alert?.status === "Ok" ? (
                                <Badge color="success" className="blog-badge">{"Resolved"}</Badge>
                              ) : (
                                <Badge color="warning" className="blog-badge">{alert?.status}</Badge>
                              )}
                            </Col>
                          </Row>
                        </Col>
                        <Col xs="8" className="ps-4 ps-md-0 text-start">
                          <Row className="d-flex align-items-center">
                            <Col xs="3">Source</Col>
                            <Col xs="5" className="text-start">
                              <img
                                src={getIntegrationLogo(alert?.integration_type)}
                                alt={alert?.integration_type}
                                className="avatar-xs rounded-circle"
                              />
                            </Col>
                          </Row>
                        </Col>
                      </Row>

                      <Row className="p-2">
                        <Col xs="4">Name</Col>
                        <Col xs="8"
                             className="ps-4 ps-md-0 text-start"
                             id='alert-name'
                             onMouseEnter={() => handleMouseEnter(alert?.name)}
                             onMouseLeave={() => handleMouseLeave(alert?.name)}>
                          {alert?.name?.length > 30 ? alert?.name?.slice(0, 30) + "..." : alert?.name}</Col>
                        <Popover isOpen={popoverOpen[alert?.name] || false}
                                 target='alert-name' placement="top">
                          <PopoverBody>{alert?.name}</PopoverBody>
                        </Popover>
                      </Row>

                      <Row className="p-2">
                        <Col xs="4">Description</Col>
                        <Col xs="8" className="ps-4 ps-md-0 text-start custom-wrap">{alert?.description}</Col>
                      </Row>

                      <Row className="p-2">
                        <Col xs="4">Application</Col>
                        <Col xs="8" className="ps-4 ps-md-0 text-start">
                          <Link to={`/temperstack-notifications/${alert?.application_service_id}`} className="link-primary">
                            {alert?.application_service.name}
                          </Link>
                        </Col>
                      </Row>

                      <Row className="p-2">
                        <Col xs="4">Metric</Col>
                        <Col xs="8" className="ps-4 ps-md-0 text-start">{alert?.metric_name}</Col>
                      </Row>

                      <Row className="p-2">
                        <Col xs="4">Resource</Col>
                        <Col xs="8" className="text-start ps-4 ps-md-0 custom-wrap">
                          <Link to={alert?.resource_url}>
                            {alert?.resource_name}
                          </Link>
                        </Col>
                      </Row>

                      <Row className="p-2">
                        <Col xs="4">Opened At</Col>
                        <Col xs="8" className="ps-4 ps-md-0 text-start">{getLocalDateTime(alert?.opened_at)}</Col>
                      </Row>

                      <Row className="p-2">
                        <Col xs="4">Assigned To</Col>
                        <Col xs="8" className="ps-4 ps-md-0 text-start">{alert?.assigned_to?.name}</Col>
                      </Row>

                      <Row className="p-2">
                        <Col xs="4">Assigned At</Col>
                        <Col xs="8" className="ps-4 ps-md-0 text-start">{getLocalDateTime(alert?.assigned_at)}</Col>
                      </Row>

                      <Row className="p-2">
                        <Col xs="4">Acknowledged At</Col>
                        <Col xs="8" className="ps-4 ps-md-0 text-start">{getLocalDateTime(alert?.acked_at)}</Col>
                      </Row>

                      <Row className="p-2">
                        <Col xs="4">Resolved At </Col>
                        <Col xs="8" className="ps-4 ps-md-0 text-start">{getLocalDateTime(alert?.closed_at)}</Col>
                      </Row>
                    </Container>
                    <div style={{width: '50%'}}>
                      <div style={{display: modalOpenAnalysis ? 'none' : 'block'}}>
                        <RootCauseAnalysis
                          showAllMetricsComponents={false}
                          render_view_metrics_button={false}
                          render_full_information={false}
                          restrictHeight={true}
                        />
                        <div style={{textAlign: 'center', marginTop: '1rem'}}>
                          <Button
                            color="primary"
                            onClick={handleViewAnalysisClick}
                          >
                            View more
                          </Button>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="w-100 d-none d-md-block">
                    <CreateRunbook alert_log_id={id}/>
                  </div>
                )}
              </div>
              <div className="m-0"
                   style={{width: '100%', position: 'relative'}}>
                <h4>Logs</h4>
                <>
                  <div className={'w-100'}>
                    <Nav pills className="nav-item-custom_alert_log">
                      {availableLogs.length > 0 ? (
                        availableLogs.map(({key, label, tabId}) => (
                          <NavItem
                            key={key}
                            className="nav-item-custom_alert_log"
                            style={{width: `${widthPercentage}%`}}
                          >
                            <NavLink
                              className={classnames({active: activeTab === tabId})}
                              onClick={() => toggleTab(tabId)}
                            >
                              <span className="d-sm-block">
                                {get_integration_logo(key)}
                                {label}
                              </span>
                            </NavLink>
                          </NavItem>
                        ))
                      ) : (
                        <div className="text-center p-4 text-white">
                          <i className="bx bx-info-circle me-2"></i>
                          No logging systems integrated. Please integrate logging systems to view logs. Integrate {' '}
                          <a href="/onboarding" target="_blank" rel="noopener noreferrer" style={{textDecoration: 'underline'}}>
                            here
                          </a>.
                        </div>
                      )}
                    </Nav>
                    <hr style={{border: "1px solid #556ee6", backgroundColor: "#556ee6", width: "100%", marginTop: "-1px",}}/>
                    <TabContent activeTab={activeTab} className="py-2">
                      <TabPane tabId={'1'}>
                        {error_logs_by_alert_log[activeTabName]?.length > 0 ? (
                          <>
                            <div className="table-responsive">
                              <div className="header d-flex border-bottom border-secondary p-2">
                                <div className="col-6 text-white  text-center fw-semibold">Message</div>
                                <div className="col-2 text-white fw-semibold text-center">Newrelic Source</div>
                                <div className="col-1 text-white fw-semibold text-center">Log Type</div>
                                <div className="col-2 text-white fw-semibold">Timestamp</div>
                                <div className="col-1 text-white fw-semibold text-center"></div>
                              </div>

                              <div className="card-container_alert_log">
                                {error_logs_by_alert_log[activeTabName]?.map((log, index) => (
                                  <div key={index} className="log-item border-bottom border-0">
                                    <div
                                      className="d-flex align-items-center p-2 cursor-pointer hover-overlay"
                                      onClick={() => toggleExpandLog(index)}
                                      style={{transition: 'background-color 0.15s ease'}}
                                    >
                                      <div className="col-6 text-white text-sm" style={{wordBreak: 'break-word'}}>
                                        <div className="text-truncate">{log.message}</div>
                                      </div>
                                      <div className="col-2 text-center text-white text-sm">
                                        {log[`newrelic.source`]}
                                      </div>
                                      <div className="col-1 d-flex justify-content-center">
                                      <span className="badge bg-secondary rounded-pill px-2 py-1 text-xs">
                                        {log.logtype}
                                      </span>
                                      </div>
                                      <div className="col-2 text-white text-sm">{getLocalDateTime(log.timestamp)}</div>
                                      <div className="col-1 text-center text-white">
                                        <i className={`bx ${expandedLogs.has(index) ? "bx-chevron-up" : "bx-chevron-down"}`}/>
                                      </div>
                                    </div>
                                    <Collapse isOpen={expandedLogs.has(index)}>
                                      <div className="p-2 mx-2 mb-2 rounded bg-black">
                                        <pre className="mb-0 text-white text-xs" style={{
                                          padding: "10px",
                                          borderRadius: "5px",
                                          whiteSpace: "pre-wrap",
                                          wordBreak: "break-word"
                                        }}>
                                          {JSON.stringify(log, null, 2)}
                                        </pre>
                                      </div>
                                    </Collapse>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="text-center p-4 text-white">
                            <i className="bx bx-info-circle me-2"></i>
                            No logs available
                          </div>
                        )}
                      </TabPane>
                      <TabPane tabId={'2'}>
                        {error_logs_by_alert_log[activeTabName]?.length > 0 ? (
                          <div className="table-responsive">
                            <div className="header d-flex border-bottom border-secondary p-2">
                              <div className="col-2 text-white fw-semibold text-center">Event Name</div>
                              <div className="col-2 text-white fw-semibold text-center">Event Time</div>
                              <div className="col-1 text-white fw-semibold text-center">Username</div>
                              <div className="col-1 text-white fw-semibold text-center">Region</div>
                              <div className="col-3 text-white fw-semibold text-center">Resource Type</div>
                              <div className="col-3 text-white fw-semibold text-center">Resource Name</div>
                            </div>

                            <div className="card-container_alert_log">
                              {error_logs_by_alert_log[activeTabName]?.map((log, index) => (
                                <div key={index} className="log-item border-bottom border-0">
                                  {log?.integration_enabled ? (
                                    <div className="d-flex align-items-center p-2 hover-overlay" style={{transition: 'background-color 0.15s ease'}}>
                                      <div className="col-2 text-white text-sm text-center text-truncate">{log.event_name}</div>
                                      <div className="col-2 text-white text-sm text-center text-truncate">{getLocalDateTime(log.event_time)}</div>
                                      <div
                                        id={`username_${index}`}
                                        className="col-1 text-white text-sm text-center text-truncate">{log.username}</div>
                                      <UncontrolledTooltip placement="top" target={`username_${index}`}>
                                        {log.username}
                                      </UncontrolledTooltip>
                                      <div

                                        className="col-1 ms-2 text-white text-sm text-center text-truncate">{log.region}</div>
                                      <div
                                        id={`resource_type_${index}`}
                                        className="col-3 text-white text-sm text-center text-truncate">{log.resource_type}</div>
                                      <UncontrolledTooltip placement="top" target={`resource_type_${index}`}>
                                        {log.resource_type}
                                      </UncontrolledTooltip>
                                      <div
                                        id={`resource_name_${index}`}
                                        className="col-3 text-white text-sm text-center text-truncate">{log.resource_name}</div>
                                      <UncontrolledTooltip placement="top" target={`resource_name_${index}`}>
                                        {log.resource_name}
                                      </UncontrolledTooltip>
                                    </div>
                                  ) : (
                                    <div
                                      id={`no_permission_${index}`}
                                      className="d-flex flex-column align-items-center p-3 border rounded shadow-sm"
                                    >
                                    <span className="text-danger fw-bold d-flex align-items-center">
                                      <i className="bx bx-error-circle fs-4 me-2"></i>
                                      Permissions Missing for LookEvents CloudTrail
                                    </span>
                                      <UncontrolledTooltip placement="top" target={`no_permission_${index}`}>
                                        {`Add "cloudtrail:LookupEvents" permission to your IAM policy JSON`}
                                      </UncontrolledTooltip>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center p-4 text-white">
                            <i className="bx bx-info-circle me-2"></i>
                            No CloudTrail logs available
                          </div>
                        )}
                      </TabPane>
                      <TabPane tabId={'3'}>
                        {error_logs_by_alert_log[activeTabName]?.length > 0 ? (
                          <>
                            <div className="table-responsive">
                              <div className="header d-flex border-bottom border-secondary p-2">
                                <div className="col-6 text-white text-center fw-semibold">Title</div>
                                <div className="col-2 text-white fw-semibold text-center">Project Name</div>
                                <div className="col-1 text-white fw-semibold text-center">Type</div>
                                <div className="col-2 text-white fw-semibold">Last Seen</div>
                                <div className="col-1 text-white fw-semibold text-center"></div>
                              </div>

                              <div className="card-container_alert_log">
                                {error_logs_by_alert_log[activeTabName]?.map((log, index) => (
                                  <div key={index} className="log-item border-bottom border-0">
                                    <div
                                      className="d-flex align-items-center p-2 cursor-pointer hover-overlay"
                                      onClick={() => toggleExpandLog(index)}
                                      style={{transition: 'background-color 0.15s ease'}}
                                    >
                                      <div className="col-6 text-white text-sm" style={{wordBreak: 'break-word'}}>
                                        <div
                                          id={`log_title_${index}`}
                                          className="d-flex align-items-center gap-2"
                                        >
                                          <span className="text-truncate" style={{maxWidth: 'calc(100% - 60px)'}}>{log.title}</span>
                                          {log.permalink && (
                                            <i
                                              className="bx bx-link-external text-primary"
                                              style={{
                                                cursor: 'pointer',
                                                fontSize: '16px',
                                                flexShrink: 0
                                              }}
                                              onClick={() => window.open(log.permalink, '_blank', 'noopener,noreferrer')}
                                            />
                                          )}
                                        </div>
                                        <UncontrolledTooltip placement="top" target={`log_title_${index}`}>
                                          {log.title}
                                        </UncontrolledTooltip>
                                      </div>
                                      <div className="col-2 text-center text-white text-sm">
                                        {log.project_name}
                                      </div>
                                      <div className="col-1 d-flex justify-content-center">
                                        <span className="badge bg-secondary rounded-pill px-2 py-1 text-xs">
                                          {log.type}
                                        </span>
                                      </div>
                                      <div className="col-2 text-white text-sm">
                                        {getLocalDateTime(log.lastSeen)}
                                      </div>
                                      <div className="col-1 text-center text-white">
                                        <i className={`bx ${expandedLogs.has(index) ? "bx-chevron-up" : "bx-chevron-down"}`}/>
                                      </div>
                                    </div>
                                    <Collapse isOpen={expandedLogs.has(index)}>
                                      <div className="p-2 mx-2 mb-2 rounded bg-black">
                                        <pre className="mb-0 text-white text-xs" style={{
                                          padding: "10px",
                                          borderRadius: "5px",
                                          whiteSpace: "pre-wrap",
                                          wordBreak: "break-word"
                                        }}>
                                          {JSON.stringify(
                                            Object.fromEntries(
                                              Object.entries(log).filter(([key]) => key !== 'hourly_stats')
                                            ), null, 2
                                          )}
                                        </pre>
                                      </div>
                                    </Collapse>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="text-center p-4 text-white">
                            <i className="bx bx-info-circle me-2"></i>
                            No Sentry logs available
                          </div>
                        )}
                      </TabPane>
                      <TabPane tabId={'4'}>
                        {error_logs_by_alert_log[activeTabName]?.length > 0 ? (
                          <>
                            <div className="table-responsive">
                              <div className="header d-flex border-bottom border-secondary p-2">
                                <div className="col-3 text-white fw-semibold">Timestamp</div>
                                <div className="col-2 text-white fw-semibold text-center">Detected Level</div>
                                <div className="col-2 text-white fw-semibold text-center">Job</div>
                                <div className="col-4 text-white text-center fw-semibold">Log</div>
                                <div className="col-1 text-white fw-semibold text-center"></div>
                              </div>

                              <div className="card-container_alert_log">
                                {error_logs_by_alert_log[activeTabName]?.map((log, index) => (
                                  <div key={index} className="log-item border-bottom border-0">
                                    <div
                                      className="d-flex align-items-center p-2 cursor-pointer hover-overlay"
                                      onClick={() => toggleExpandLog(index)}
                                      style={{transition: 'background-color 0.15s ease'}}
                                    >
                                      <div className="col-3 text-white text-sm">
                                        {getLocalDateTime(log.timestamp)}
                                      </div>
                                      <div className="col-2 d-flex justify-content-center">
                                        <span
                                          className={`badge ${log.metadata?.detected_level === 'error' ? 'bg-danger' : 'bg-secondary'} rounded-pill px-2 py-1 text-xs`}>
                                          {log.metadata?.detected_level || 'unknown'}
                                        </span>
                                      </div>
                                      <div className="col-2 text-center text-white text-sm">
                                        {log.metadata?.job || 'N/A'}
                                      </div>
                                      <div className="col-4 text-white text-sm" style={{wordBreak: 'break-word'}}>
                                        <div className="text-truncate">{log.log}</div>
                                      </div>
                                      <div className="col-1 text-center text-white">
                                        <i className={`bx ${expandedLogs.has(index) ? "bx-chevron-up" : "bx-chevron-down"}`}/>
                                      </div>
                                    </div>
                                    <Collapse isOpen={expandedLogs.has(index)}>
                                      <div className="p-2 mx-2 mb-2 rounded bg-black">
                                        <pre className="mb-0 text-white text-xs" style={{
                                          padding: "10px",
                                          borderRadius: "5px",
                                          whiteSpace: "pre-wrap",
                                          wordBreak: "break-word"
                                        }}>
                                          {JSON.stringify(log, null, 2)}
                                        </pre>
                                      </div>
                                    </Collapse>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="text-center p-4 text-white">
                            <i className="bx bx-info-circle me-2"></i>
                            No Loki logs available
                          </div>
                        )}
                      </TabPane>
                      <TabPane tabId={'5'}>
                        {error_logs_by_alert_log[activeTabName]?.length > 0 ? (
                          <>
                            <div className="table-responsive">
                              <div className="header d-flex border-bottom border-secondary p-2">
                                <div className="col-3 text-white fw-semibold">Timestamp</div>
                                <div className="col-2 text-white fw-semibold text-center">Severity</div>
                                <div className="col-2 text-white fw-semibold text-center">Subsystem</div>
                                <div className="col-4 text-white text-center fw-semibold">Log</div>
                                <div className="col-1 text-white fw-semibold text-center"></div>
                              </div>
                              <div className="card-container_alert_log">
                                {error_logs_by_alert_log[activeTabName]?.map((log, index) => (
                                  <div key={index} className="log-item border-bottom border-0">
                                    <div
                                      className="d-flex align-items-center p-2 cursor-pointer hover-overlay"
                                      onClick={() => toggleExpandLog(index)}
                                      style={{transition: 'background-color 0.15s ease'}}
                                    >
                                      <div className="col-3 text-white text-sm">
                                        {formatTimestampToLogTime(log.timestamp)}
                                      </div>
                                      <div className="col-2 d-flex justify-content-center">
                                        <span className={`badge ${
                                          log.severity === '6' ? 'bg-danger' :
                                            log.severity === '5' ? 'bg-warning text-dark' :
                                              log.severity === '4' ? 'bg-info text-dark' :
                                                'bg-secondary'
                                        } rounded-pill px-2 py-1 text-xs`}>
                                          {log.severity === '6' ? 'critical' :
                                            log.severity === '5' ? 'error' :
                                              log.severity === '4' ? 'warning' :
                                                'info'}
                                        </span>
                                      </div>
                                      <div className="col-2 text-center text-white text-sm">
                                        {log.subsystem || 'N/A'}
                                      </div>
                                      <div className="col-4 text-white text-sm" style={{wordBreak: 'break-word'}}>
                                        <div className="text-truncate">{log.log}</div>
                                      </div>
                                      <div className="col-1 text-center text-white">
                                        <i className={`bx ${expandedLogs.has(index) ? "bx-chevron-up" : "bx-chevron-down"}`}/>
                                      </div>
                                    </div>
                                    <Collapse isOpen={expandedLogs.has(index)}>
                                      <div className="p-2 mx-2 mb-2 rounded bg-black">
                                        <pre className="mb-0 text-white text-xs" style={{
                                          padding: "10px",
                                          borderRadius: "5px",
                                          whiteSpace: "pre-wrap",
                                          wordBreak: "break-word"
                                        }}>
                                          {JSON.stringify(log, null, 2)}
                                        </pre>
                                      </div>
                                    </Collapse>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="text-center p-4 text-white">
                            <i className="bx bx-info-circle me-2"></i>
                            No Coralogix logs available
                          </div>
                        )}
                      </TabPane>
                      <TabPane tabId={'6'}>
                        {recentChangeEvents?.length > 0 ? (
                          <>
                            <div className="table-responsive">
                              <div className="header d-flex border-bottom border-secondary p-2">
                                <div className="col-3 text-white fw-semibold">Name</div>
                                <div className="col-3 text-white fw-semibold text-center">Deployed At</div>
                                <div className="col-3 text-white fw-semibold text-center">Deployed By</div>
                                <div className="col-1 text-white fw-semibold text-center"></div>
                              </div>
                              <div className="card-container_alert_log">
                                {recentChangeEvents?.map((event, index) => (
                                  <div key={event.id} className="log-item border-bottom border-0">
                                    <div
                                      className="d-flex align-items-center p-2 cursor-pointer hover-overlay"
                                      onClick={() => toggleExpandLog(index)}
                                      style={{transition: 'background-color 0.15s ease'}}
                                    >
                                      <div className="col-4 text-white text-sm">{event.name}</div>
                                      <div className="col-3 text-center text-white text-sm">
                                        {getLocalDateTime(event.created_at)}
                                      </div>
                                      <div className="col-3 text-center text-white text-sm">
                                        {event.changed_by}
                                      </div>
                                      <div className="col-1 text-center text-white">
                                        <i className={`bx ${expandedLogs.has(index) ? "bx-chevron-up" : "bx-chevron-down"}`}/>
                                      </div>
                                    </div>
                                    <Collapse isOpen={expandedLogs.has(index)}>
                                      <div className="p-2 mx-2 mb-2 rounded bg-black">
                                            <pre className="mb-0 text-white text-xs" style={{
                                              padding: "10px",
                                              borderRadius: "5px",
                                              whiteSpace: "pre-wrap",
                                              wordBreak: "break-word"
                                            }}>
                                              {(() => {
                                                const changeData = event.change_data || {};
                                                const name = event.name || 'N/A';
                                                const environment = changeData.environment || 'N/A';
                                                const changes = changeData.changes || [];
                                                return `Name: ${name}\n` +
                                                  `Environment: ${environment}\n` +
                                                  (changes.length > 0
                                                    ? `Changes:\n` + changes.map(item => `  • ${item}`).join('\n')
                                                    : 'No changes recorded');
                                              })()}
                                            </pre>
                                      </div>
                                    </Collapse>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="text-center p-4 text-white">
                            <i className="bx bx-info-circle me-2"></i>
                            No Recent Deployments here. <br/>
                            If not integrated, please integrate {' '}
                            <a href="/organization?tab=6" target="_blank" rel="noopener noreferrer" style={{textDecoration: 'underline'}}>
                              here
                            </a>.
                          </div>
                        )}
                      </TabPane>
                    </TabContent>
                  </div>
                </>
              </div>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
}

AlertLogDetails.propTypes = {
  props: PropTypes.array,
  runbookDetails: PropTypes.array
};

export default AlertLogDetails;
